/**
 * Near-duplicate detection for custom operating-expense names.
 *
 * The live list drifted into three kinds of duplicate that plain equality
 * misses, and all three read as one line to anyone looking at the pie:
 *   - a typo pair       "Cloudflare"    / "Claudeflare"
 *   - a plural pair     "Tech Salary"   / "Tech Salaries"
 *   - an exact repeat   "Sentry"        / "Sentry"
 * Names are free text with no backend uniqueness rule, so the guard lives on
 * the way in: the add and edit forms warn before another one lands.
 */

/** Lowercase, drop everything that is not a letter or a digit. */
const normalize = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]/g, '');

/** The digit runs of a name, in order: "chasebank1ccpayoff51k" -> "1,51". */
const digitRuns = (normalized: string) =>
  (normalized.match(/\d+/g) || []).join(',');

/** Iterative Levenshtein over two rows. The list is tens of names long, so
 *  the plain O(n*m) version is far below anything worth optimizing. */
const editDistance = (a: string, b: string): number => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  let curr = new Array<number>(b.length + 1);

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const substitution = prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1);
      curr[j] = Math.min(substitution, prev[j] + 1, curr[j - 1] + 1);
    }
    [prev, curr] = [curr, prev];
  }

  return prev[b.length];
};

/**
 * Whether two expense names are close enough to be worth questioning.
 *
 * Longer names get one more unit of slack, because the plural pair above is
 * three edits apart ("techsalary" / "techsalaries") while a two-edit budget on
 * short names would collide on almost any vowel.
 *
 * Names whose digits differ are never similar, however close the letters are.
 * Without that rule `Chase Bank 1 - CC Pay-Off $51K` and
 * `Chase Bank 2 - CC Pay-Off $49K` - two genuinely different cards - land three
 * edits apart and would be flagged on every render.
 */
export const namesAreSimilar = (a: string, b: string): boolean => {
  const left = normalize(a);
  const right = normalize(b);
  if (!left || !right) return false;
  if (left === right) return true;
  if (digitRuns(left) !== digitRuns(right)) return false;

  const shortest = Math.min(left.length, right.length);
  return editDistance(left, right) <= (shortest >= 8 ? 3 : 2);
};

/**
 * The stored names that `candidate` is close to, ignoring `candidate` itself
 * when it is already one of them (an edit form re-checking its own row).
 *
 * One entry per distinct spelling, not per row: three rows all called "Tech
 * Salaries" are one thing to warn about, and printing the name three times
 * reads as noise. Names that merely resemble each other ("Cloudflare" and
 * "Claudeflare") are distinct spellings and stay listed separately, since
 * which one is meant is exactly what the admin has to decide.
 */
export const findSimilarNames = (
  candidate: string,
  existing: { id: number; name: string }[],
  ignoreId?: number
): string[] => {
  const seen = new Set<string>();
  const names: string[] = [];

  existing.forEach((e) => {
    if (e.id === ignoreId || !namesAreSimilar(candidate, e.name)) return;
    const name = e.name.trim();
    // Same collapsing rule the breakdown pie uses, so the warning lists what
    // that list will actually end up showing as one line.
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    names.push(name);
  });

  return names;
};

/**
 * Names to offer in the autocomplete while the admin types.
 *
 * A plain case-insensitive substring match, NOT the fuzzy `namesAreSimilar`
 * above: that one compares whole names to catch a typo after the fact, so it
 * stays silent on the "tech" of "Tech Salaries" and would make the dropdown
 * appear only once the name is already typed out.
 *
 * One entry per distinct spelling, and a name identical to what is in the box
 * is left out - there is nothing left to complete. Names that begin with what
 * was typed sort first, the rest alphabetically after them.
 */
export const suggestNames = (
  candidate: string,
  existing: { id: number; name: string }[],
  ignoreId?: number,
  limit = 6
): string[] => {
  const typed = candidate.trim().toLowerCase();
  if (!typed) return [];

  const seen = new Set<string>();
  const names: string[] = [];

  existing.forEach((e) => {
    if (e.id === ignoreId) return;
    const name = e.name.trim();
    const key = name.toLowerCase();
    if (key === typed || !key.includes(typed) || seen.has(key)) return;
    seen.add(key);
    names.push(name);
  });

  return names
    .sort((a, b) => {
      const aStarts = a.toLowerCase().startsWith(typed);
      const bStarts = b.toLowerCase().startsWith(typed);
      if (aStarts !== bStarts) return aStarts ? -1 : 1;
      return a.localeCompare(b);
    })
    .slice(0, limit);
};
